"use client";

import React from "react";
import { Activity, Flame, CircleCheck as CheckCircle2, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MetabolicComparison() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const vo2Points = ["VO₂ max or achieved peak oxygen uptake", "Exercise heart-rate response", "Personalised training zones", "Exercise intensity", "Calorie expenditure during different exercise intensities", "Exercise respiratory exchange data", "Fat versus carbohydrate utilisation during exercise", "Exercise thresholds identified by the CardioCoach system"];
  const rmrPoints = ["Understanding baseline energy requirements", "Planning nutrition more objectively", "Managing body composition goals", "Comparing measured metabolism with prediction equations", "Monitoring changes over time"];
  return <section id="metabolicassessmentcomparison" data-nav="light" ref={ref} className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-700 ease-out" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)"
      }}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            What would you like to understand?
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Card 1 — VO₂ Max */}
          <div className="flex transition-all duration-700 delay-100 ease-out" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            <Card className="flex-1 flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="p-2.5 rounded-lg bg-primary/10 text-primary inline-flex">
                    <Activity className="size-6" aria-hidden="true" />
                  </span>
                  <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                    VO₂ Max Assessment
                  </span>
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-semibold text-foreground leading-snug">
                  How effectively does your body use oxygen during exercise?
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-6 flex-1">
                <p className="text-base text-muted-foreground leading-relaxed">
                  VO₂ max measures the maximum amount of oxygen your body can use during
                  progressively harder exercise and is widely used as an indicator of
                  cardiorespiratory fitness and aerobic capacity.
                </p>

                <div className="border-t border-border/70 pt-6">
                  <h3 className="text-sm font-semibold text-foreground mb-4">
                    Assessment insights include:
                  </h3>
                  <ul className="space-y-3" role="list">
                    {vo2Points.map((point, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm text-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{point}</span>
                      </li>)}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Card 2 — Resting Metabolic Rate */}
          <div className="flex transition-all duration-700 delay-200 ease-out" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            <Card className="flex-1 flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="p-2.5 rounded-lg bg-primary/10 text-primary inline-flex">
                    <Flame className="size-6" aria-hidden="true" />
                  </span>
                  <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                    Resting Metabolic Rate (RMR)
                  </span>
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-semibold text-foreground leading-snug">
                  How much energy does your body use at rest?
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-6 flex-1 justify-between">
                <div>
                  <p className="text-base text-muted-foreground leading-relaxed mb-6">
                    Resting Metabolic Rate, or RMR, measures the energy your body uses
                    while resting to maintain essential functions such as breathing,
                    circulation and normal organ activity.
                  </p>

                  <div className="border-t border-border/70 pt-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4">
                      Where measured RMR can be useful:
                    </h3>
                    <ul className="space-y-3" role="list">
                      {rmrPoints.map((point, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm text-foreground/90">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 rounded-lg bg-muted/60 p-4 border border-border/80 flex items-start gap-3 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  <Info className="size-4 text-foreground/70 shrink-0 mt-0.5" aria-hidden="true" />
                  <p>
                    RMR is a measure of resting energy expenditure. It does not, by itself,
                    prescribe an individual&apos;s diet or guarantee weight loss.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}